import { Box } from '@mantine/core';
import type { InputMask } from 'imask';
import { Controller, type UseControllerProps } from 'react-hook-form';
import { IMaskInput } from 'react-imask';
import { FieldErrorMessage } from 'shared/ui/FieldErrorMessage';
import type { ValidatorMaskConfig } from 'shared/validators';
import { getInputStyles } from 'theme/components/Input';
import { useObjectTranslation } from '../Inputs/hooks';
import { useRules } from './hooks';
import type { RequiredField } from './types';

export type MaskedTextFieldProps = {
  maskConfig: ValidatorMaskConfig;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  maxLength?: number;
  label?: React.ReactNode;
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md';
  disabled?: boolean;
  withErrorWrapperAlwaysEnabled?: boolean;
} & UseControllerProps &
  RequiredField;

const SIZE_HEIGHT: Record<string, number> = {
  xs: 30,
  sm: 36,
  md: 42,
};

const MaskedInput: React.FC<{
  maskConfig: ValidatorMaskConfig;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  maxLength?: number;
  name: string;
  value: string;
  onAccept: (value: string, mask: InputMask) => void;
  onBlur: () => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  size: 'xs' | 'sm' | 'md';
}> = ({
  maskConfig,
  inputMode,
  maxLength,
  name,
  value,
  onAccept,
  onBlur,
  placeholder,
  disabled,
  error,
  size,
}) => (
  <Box
    sx={t => ({
      '& input': {
        ...getInputStyles(t),
        fontFamily: 'inherit',
        width: '100%',
        height: SIZE_HEIGHT[size],
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: t.radius.sm,
        boxSizing: 'border-box',
        outline: 'none',
        ...(error ? { borderColor: t.colors.red[6] } : {}),
        ...(disabled
          ? {
              backgroundColor: t.colors.gray[1],
              borderColor: t.colors.gray[3],
            }
          : {}),
        '&:focus': {
          ...t.focusRingStyles.inputStyles(t),
        },
      },
    })}
  >
    <IMaskInput
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(maskConfig as any)}
      unmask
      value={value}
      onAccept={onAccept}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      inputMode={inputMode}
      maxLength={maxLength}
      data-testid={`field-masked-${name}`}
    />
  </Box>
);

export const MaskedTextField: React.FC<MaskedTextFieldProps> = ({
  maskConfig,
  inputMode,
  maxLength,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  required,
  label,
  placeholder,
  size = 'sm',
  disabled,
  withErrorWrapperAlwaysEnabled = true,
  ...restProps
}) => {
  const innerRules = useRules({ required, ...rules });
  const { name } = restProps;
  const { t } = useObjectTranslation('common');

  return (
    <Controller
      rules={innerRules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const error = fieldState.error?.message;
        // eslint-disable-next-line no-nested-ternary
        const errorMessage = error
          ? t(error)
          : withErrorWrapperAlwaysEnabled
            ? ' '
            : undefined;

        return (
          <Box>
            {label && (
              <Box
                component="label"
                sx={t => ({
                  fontSize: t.fontSizes.sm,
                  fontWeight: 500,
                  color: t.black,
                  display: 'block',
                  marginBottom: 2,
                })}
              >
                {label}
              </Box>
            )}
            <MaskedInput
              maskConfig={maskConfig}
              inputMode={inputMode}
              maxLength={maxLength}
              name={name}
              value={field.value ?? ''}
              onAccept={(value, _mask) => {
                field.onChange(value === '' ? null : value);
              }}
              onBlur={field.onBlur}
              placeholder={placeholder}
              disabled={disabled}
              error={error}
              size={size}
            />
            <FieldErrorMessage>{errorMessage}</FieldErrorMessage>
          </Box>
        );
      }}
    />
  );
};
